import React, { useEffect } from "react";

import { formatMoney } from "../model/format";
import { formatMonthColumnLabel } from "../model/targets";
import { MarketTarget } from "../model/types";

interface TargetsPreviewProps {
  targets: MarketTarget[];
  onClose: () => void;
}

/**
 * The complete, unfiltered set of configured targets - a reference table for "what are the
 * targets, anyway", independent of whatever period/market the viewer currently has selected.
 */
export default function TargetsPreview(props: TargetsPreviewProps) {
  const { targets, onClose } = props;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const months = [...new Set(targets.map((target) => target.month))].sort();
  const markets = [...new Set(targets.map((target) => target.market))].sort(
    (a, b) => a.localeCompare(b),
  );

  const byMarketMonth = new Map<string, MarketTarget>();
  targets.forEach((target) => {
    byMarketMonth.set(`${target.market} ${target.month}`, target);
  });

  const cell = (
    market: string,
    month: string,
    metric: "revenue" | "profit",
  ) => {
    const value = byMarketMonth.get(`${market} ${month}`)?.[metric];
    return value === null || value === undefined ? "-" : formatMoney(value);
  };

  return (
    <div
      className="finance-reporting-modal"
      role="dialog"
      aria-modal="true"
      aria-label="All configured targets"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="finance-reporting-modal__panel">
        <div className="finance-reporting-modal__heading">
          <h3>Revenue &amp; profit targets</h3>
          <button
            type="button"
            className="finance-reporting-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {markets.length ? (
          <div className="finance-reporting-table-wrap">
            <table className="finance-reporting-table">
              <thead>
                <tr>
                  <th>Market</th>
                  <th>Measure</th>
                  {months.map((month) => (
                    <th
                      key={month}
                      className="finance-reporting-table__numeric"
                    >
                      {formatMonthColumnLabel(month)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {markets.map((market) => (
                  <React.Fragment key={market}>
                    <tr>
                      <td rowSpan={2}>{market}</td>
                      <td>Revenue</td>
                      {months.map((month) => (
                        <td
                          key={month}
                          className="finance-reporting-table__numeric"
                        >
                          {cell(market, month, "revenue")}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Profit</td>
                      {months.map((month) => (
                        <td
                          key={month}
                          className="finance-reporting-table__numeric"
                        >
                          {cell(market, month, "profit")}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="finance-reporting-empty">
            No targets configured for this workspace.
          </div>
        )}
      </div>
    </div>
  );
}
